
import React from 'react';
import { lazy } from 'react';
import { PageTypes } from 'types/pageType';
import GlobalLayout from 'components/globalLayout';

const LoginPage = lazy(() => import('containers/auth/login'));
const RegisterPage = lazy(() => import('containers/auth/register'));
const DashboardPage = lazy(() => import('containers/dashboard'));
const SalesPage = lazy(() => import('containers/sales'));

// Analytics Pages
const SalesAnalyticsPage = lazy(() => import('containers/analytics/SalesAnalytics/index'));
const InventoryAnalyticsPage = lazy(() => import('containers/inventory-management/InventoryDashboard/index'));
const CustomerAnalyticsPage = lazy(() => import('containers/analytics/CustomerAnalytics/index'));
const FinancialAnalyticsPage = lazy(() => import('containers/analytics/FinancialAnalytics/index'));
const StockMovementsPage = lazy(() => import('containers/inventory-management/StockMovements/index'));

const InventoryPage = lazy(() => import('containers/inventory'));
const ProductsPage = lazy(() => import('containers/stock-management'));
const FundsPage = lazy(() => import('containers/fund'));
const ExpensesPage = lazy(() => import('pages/expenses'));
const ArchivedStockPage = lazy(
  () => import('containers/products/ArchivedStock')
);
const SettingPage = lazy(() => import('containers/settings'));
const ClientPage = lazy(() => import('containers/clients'));
const ProductCategoryPage = lazy(() => import('containers/productCategory'));
const DebtPage = lazy(() => import('containers/debts'));
const ForgotPasswordPage = lazy(() => import('containers/auth/forgotPassword'));
const PageNotFound = lazy(() => import('pages/PageNotFound'));
const SuppliersPage = lazy(() => import('containers/suppliers'));
const ManageBranchsPage = lazy(() => import('containers/manageBranchs'));
const StockPage = lazy(() => import('containers/stock-management'));
const FulfillmentPage = lazy(() => import('containers/stock/Fulfillment'));
const SalesPageV2 = lazy(() => import('containers/sales/pos_v2'));

// Domain Microservices Containers
// Coffee Shop Microservice Containers
const CoffeeShopDashboardPage = lazy(() => import('containers/coffee-shop/dashboard'));
const CoffeeShopPOSPage = lazy(() => import('containers/coffee-shop/pos'));
const CoffeeShopOrdersPage = lazy(() => import('containers/coffee-shop/orders'));
const CoffeeShopRecipesPage = lazy(() => import('containers/coffee-shop/recipes'));
const CoffeeShopModifiersPage = lazy(() => import('containers/coffee-shop/modifiers'));
const CoffeeShopShiftsPage = lazy(() => import('containers/coffee-shop/shifts'));
const CoffeeShopSpillagePage = lazy(() => import('containers/coffee-shop/spillage'));
const CoffeeShopLoyaltyPage = lazy(() => import('containers/coffee-shop/loyalty'));

const GroceryPage = lazy(() => import('containers/grocery'));
const GroceryDashboardPage = lazy(() => import('containers/grocery/dashboard'));
const GroceryPOSPage = lazy(() => import('containers/grocery/pos'));
const GroceryInventoryPage = lazy(() => import('containers/grocery/inventory'));
const GroceryExpiriesPage = lazy(() => import('containers/grocery/expiries'));
const GrocerySpoilagePage = lazy(() => import('containers/grocery/spoilage'));
const GrocerySalesHistoryPage = lazy(() => import('containers/grocery/sales-history'));
const GroceryReportsPage = lazy(() => import('containers/grocery/reports'));
const ClothingPage = lazy(() => import('containers/clothing'));
const ShoeDashboardPage = lazy(() => import('containers/shoe/dashboard'));
const ShoeCatalogPage = lazy(() => import('containers/shoe'));
const ShoePrepacksPage = lazy(() => import('containers/shoe/prepacks'));
const ShoeAuditsPage = lazy(() => import('containers/shoe/audits'));
const ShoeMismatchedPage = lazy(() => import('containers/shoe/mismatched'));
const ShoeCarePage = lazy(() => import('containers/shoe/care'));
const ShoeLabelsPage = lazy(() => import('containers/shoe/labels'));
const ShoeSizeConverterPage = lazy(() => import('containers/shoe/size-converter'));
const ShoePOSPage = lazy(() => import('containers/shoe/pos'));
const ShoeSalesHistoryPage = lazy(() => import('containers/shoe/sales-history'));
const ShoeDebtsPage = lazy(() => import('containers/shoe/debts'));
const HardwarePage = lazy(() => import('containers/hardware'));
const HardwareDashboardPage = lazy(() => import('containers/hardware/dashboard'));
const HardwareRentalsPage = lazy(() => import('containers/hardware/rentals'));
const HardwareDebtsPage = lazy(() => import('containers/hardware/debts'));
const HardwareAnalyticsPage = lazy(() => import('containers/hardware/analytics'));
const HardwareCustomersPage = lazy(() => import('containers/hardware/customers'));
const HardwareReportsPage = lazy(() => import('containers/hardware/reports'));
const JewelryPage = lazy(() => import('containers/jewelry/dashboard'));
const JewelryVaultPage = lazy(() => import('containers/jewelry/vault'));
const JewelryPOSPage = lazy(() => import('containers/jewelry/pos'));
const JewelrySalesHistoryPage = lazy(() => import('containers/jewelry/sales-history'));
const JewelryCertificatesPage = lazy(() => import('containers/jewelry/certificates'));
const JewelryTradeInsPage = lazy(() => import('containers/jewelry/trade-ins'));
const JewelrySpotRatesPage = lazy(() => import('containers/jewelry/spot-rates'));
const JewelryReportsPage = lazy(() => import('containers/jewelry/reports'));
const HotelPage = lazy(() => import('containers/hotel'));
const HotelDashboardPage = lazy(() => import('containers/hotel/pages/HotelDashboardPage'));
const HotelRoomsPage = lazy(() => import('containers/hotel/pages/HotelRoomsPage'));
const HotelReservationsPage = lazy(() => import('containers/hotel/pages/HotelReservationsPage'));
const HotelHousekeepingPage = lazy(() => import('containers/hotel/pages/HotelHousekeepingPage'));
const HotelFoliosPage = lazy(() => import('containers/hotel/pages/HotelFoliosPage'));
const HotelReportsPage = lazy(() => import('containers/hotel/pages/HotelReportsPage'));
const HotelGuestsPage = lazy(() => import('containers/hotel/pages/HotelGuestsPage'));

// 7 New Business Vertical Microservice Containers
const RealEstateWorkspacePage = lazy(() => import('containers/real-estate'));
const BarbershopWorkspacePage = lazy(() => import('containers/barbershop'));
const SpaWorkspacePage = lazy(() => import('containers/spa'));
const TailoringWorkspacePage = lazy(() => import('containers/tailoring'));
const ProfessionalServicesWorkspacePage = lazy(() => import('containers/professional-services'));
const SalonWorkspacePage = lazy(() => import('containers/salon'));
const CarRentalWorkspacePage = lazy(() => import('containers/car-rental'));

// Mobile Money & Agency Banking
const MobileMoneyDashboardPage = lazy(() => import('containers/mobile-money/Dashboard'));
const MobileMoneyPOSPage = lazy(() => import('containers/mobile-money/POS'));
const MobileMoneyFloatsPage = lazy(() => import('containers/mobile-money/Floats'));
const MobileMoneyLedgerPage = lazy(() => import('containers/mobile-money/Ledger'));
const MobileMoneyReconciliationPage = lazy(() => import('containers/mobile-money/Reconciliation'));
const MobileMoneyDebtsPage = lazy(() => import('containers/mobile-money/Debts'));
const MobileMoneyCustomerHistoryPage = lazy(() => import('containers/mobile-money/CustomerHistory'));
const MobileMoneyReportsPage = lazy(() => import('containers/mobile-money/Reports'));
const WholesalePage = lazy(() => import('containers/sales/wholesale'));
const PurchaseOrdersPage = lazy(
  () => import('containers/procurement/purchaseOrders/index')
);
const CreatePurchaseOrderPage = lazy(
  () => import('containers/procurement/purchaseOrders/CreatePurchaseOrder')
);
const DeliveryNotesPage = lazy(
  () => import('containers/procurement/deliveryNotes/index')
);
const CreateDeliveryNotePage = lazy(
  () => import('containers/procurement/deliveryNotes/CreateDeliveryNote')
);
const PharmacyDashboardPage = lazy(
  () => import('containers/pharmacy/dashboard/index')
);
const PrescriptionsPage = lazy(
  () => import('containers/pharmacy/prescriptions/index')
);
const MedicationsPage = lazy(
  () => import('containers/pharmacy/medications/index')
);
const PharmacistsPage = lazy(
  () => import('containers/pharmacy/pharmacists/index')
);
const CreatePatientPage = lazy(
  () => import('containers/pharmacy/patients/CreatePatient')
);
const PatientsPage = lazy(() => import('containers/pharmacy/patients/index'));
const PatientHistoryPage = lazy(() => import('containers/pharmacy/patients/PatientHistory'));
const PharmacyBatchesPage = lazy(() => import('containers/pharmacy/batches/BatchList'));
const PharmacyPOSPage = lazy(() => import('containers/pharmacy/pos/PharmacyPOS'));
const PharmacySalesHistoryPage = lazy(() => import('containers/pharmacy/sales-history/index'));
const PharmacyBranchesPage = lazy(() => import('containers/pharmacy/branches/BranchManagement'));
const PharmacyReportsPage = lazy(() => import('containers/pharmacy/reports/PharmacyReports'));
const PharmacyDispensingPage = lazy(() => import('containers/pharmacy/dispensing/index'));
// Pharmacy Procurement
const PharmacyPurchaseOrdersPage = lazy(() => import('containers/pharmacy/purchase-orders/PurchaseOrderList'));
const PharmacyPurchaseOrderDetailPage = lazy(() => import('containers/pharmacy/purchase-orders/PurchaseOrderDetail'));
const PharmacyStockTransfersPage = lazy(() => import('containers/pharmacy/stock-transfers/StockTransferList'));
const PharmacyCreateStockTransferPage = lazy(() => import('containers/pharmacy/stock-transfers/CreateStockTransfer'));
const PharmacyStockTransferDetailPage = lazy(() => import('containers/pharmacy/stock-transfers/StockTransferDetail'));
const PharmacyGoodsReceivingCreatePage = lazy(() => import('containers/pharmacy/goods-receiving/GoodsReceivingCreate'));
const PharmacyGoodsReceivingDetailPage = lazy(() => import('containers/pharmacy/goods-receiving/GoodsReceivingDetail'));
const PharmacyGoodsReceivingPage = lazy(() => import('containers/pharmacy/goods-receiving/GoodsReceivingList'));

// Hospital & Clinical
const HospitalQueuePage = lazy(() => import('containers/hospital/queue/index'));
const HospitalTriagePage = lazy(() => import('containers/hospital/triage/index'));
const HospitalConsultationPage = lazy(() => import('containers/hospital/consultation/index'));
const HospitalDashboardPage = lazy(() => import('containers/hospital/dashboard/index'));
const HospitalReportsPage = lazy(() => import('containers/hospital/reports/index'));
const HospitalWardsPage = lazy(() => import('containers/hospital/wards/index'));
const HospitalLabsPage = lazy(() => import('containers/hospital/lab/index'));
const HospitalAppointmentsPage = lazy(() => import('containers/hospital/appointments/index'));
const HospitalFinancePage = lazy(() => import('containers/hospital/finance/index'));
const HospitalPACSPage = lazy(() => import('containers/hospital/pacs/index'));
const HospitalSettingsPage = lazy(() => import('containers/hospital/settings/index'));
const HospitalAmbulancePage = lazy(() => import('containers/hospital/ambulance/index'));

// Retail (shop, boutique, store, supermarket)
const RetailInventoryPage = React.lazy(() => import('containers/retail/inventory'));
const RetailSuppliersPage = React.lazy(() => import('containers/retail/suppliers'));
const RetailReportsPage = React.lazy(() => import('containers/retail/reports'));

// Branch Management pages
const BranchPricingPage = React.lazy(() => import('containers/branches/pricing'));
const BranchTransfersPage = React.lazy(() => import('containers/branches/transfers'));

const BarDashboardPage = lazy(() => import('containers/bar/dashboard/index'));
const BarPOSPage = lazy(() => import('containers/bar/pos/index'));
const ComptoirsPage = lazy(() => import('containers/bar/comptoirs/index'));
const BarStockPage = lazy(() => import('containers/bar/stock/index'));
const BarMovementsPage = lazy(() => import('containers/bar/movements/index'));
const BarRecipesPage = lazy(() => import('containers/bar/recipes/index'));
const BarRecipeDetailsPage = lazy(() => import('containers/bar/recipes/RecipeDetails'));
const BarCreateRecipePage = lazy(() => import('containers/bar/recipes/CreateRecipe'));
const BarTabsPage = lazy(() => import('containers/bar/tabs/index'));
const BarShiftsPage = lazy(() => import('containers/bar/shifts/index'));
const BarAnalyticsPage = lazy(() => import('containers/bar/analytics/index'));
const BarMenusPage = lazy(() => import('containers/bar/menus/index'));
const BarManageMenuPage = lazy(() => import('containers/bar/menus/ManageMenu'));
const BarBottlesPage = lazy(() => import('containers/bar/bottles/index'));

const RestaurantDashboardPage = lazy(() => import('containers/restaurant/dashboard/index'));
const RestaurantTablesPage = lazy(() => import('containers/restaurant/tables/index'));
const RestaurantReservationsPage = lazy(() => import('containers/restaurant/reservations/index'));
const RestaurantKitchenPage = lazy(() => import('containers/restaurant/kitchen/index'));
const RestaurantTableReportsPage = lazy(() => import('containers/restaurant/reports/TableSessions'));
const GuestMenuPage = lazy(() => import('containers/restaurant/order/GuestMenu'));
const RestaurantTableTermsPage = lazy(() => import('containers/restaurant/tables/TableTerms'));

const CreatePrescriptionPage = lazy(
  () => import('containers/pharmacy/prescriptions/CreatePrescription')
);

const AuditLogsPage = lazy(() => import('pages/reports/AuditLogs'));
const ReportsPage = lazy(() => import('containers/reports/SalesReportsDashboard/index'));

// Owner Dashboard (cross-branch global views)
const OwnerDashboardPage = lazy(() => import('containers/owner/dashboard/index'));

// Website Management (Fukkoni marketing site)
const WebsitePricingPlansPage = lazy(() => import('containers/website/pricing-plans/index'));
const WebsiteVisitorsPage = lazy(() => import('containers/website/visitors/index'));
const SubscriptionRenewPage = lazy(() => import('containers/auth/renew/index'));

// Super Admin Portal
const SuperAdminLoginPage = lazy(() => import('containers/super-admin/login/index'));
const SuperAdminDashboardPage = lazy(() => import('containers/super-admin/dashboard/index'));
const SuperAdminShopsPage = lazy(() => import('containers/super-admin/shops/index'));
const SuperAdminUsersPage = lazy(() => import('containers/super-admin/users/index'));
const SuperAdminActivityPage = lazy(() => import('containers/super-admin/activity/index'));
const SuperAdminPaymentsPage = lazy(() => import('containers/super-admin/payments/index'));
const SuperAdminSettingsPage = lazy(() => import('containers/super-admin/settings/index'));
const SuperAdminStorefrontsPage = lazy(() => import('containers/super-admin/storefronts/index'));
const MerchantStorefrontPage = lazy(() => import('containers/storefront-merchant'));

const HRMEmployeesPage = lazy(() => import('containers/hrm/employees/index'));
const HRMPayrollPage = lazy(() => import('containers/hrm/payroll/index'));


const FukoWebAppPages: PageTypes = {
  LoginPage: <LoginPage />,
  RegisterPage: <RegisterPage />,
  DashboardPage: <DashboardPage />,
  SalesPage: <SalesPage />,
  InventoryPage: <InventoryPage />,
  ProductsPage: <ProductsPage />,
  FundsPage: <FundsPage />,
  ArchivedStockPage: <ArchivedStockPage />,
  SettingPage: <SettingPage />,
  ClientPage: <ClientPage />,
  ProductCategoryPage: <ProductCategoryPage />,
  DebtPage: <DebtPage />,
  ForgotPasswordPage: <ForgotPasswordPage />,
  PageNotFound: <PageNotFound />,
  SuppliersPage: <SuppliersPage />,
  ManageBranchsPage: <ManageBranchsPage />,
  StockPage: <StockPage />,
  RetailFulfillmentPage: <FulfillmentPage />,
  SalesPageV2: <SalesPageV2 />,
  WholesalePage: <WholesalePage />,
  PurchaseOrdersPage: <PurchaseOrdersPage />,
  CreatePurchaseOrderPage: <CreatePurchaseOrderPage />,
  DeliveryNotesPage: <DeliveryNotesPage />,
  CreateDeliveryNotePage: <CreateDeliveryNotePage />,
  PharmacyDashboardPage: <PharmacyDashboardPage />,
  PrescriptionsPage: <PrescriptionsPage />,
  CreatePrescriptionPage: <CreatePrescriptionPage />,
  MedicationsPage: <MedicationsPage />,
  PharmacistsPage: <PharmacistsPage />,
  CreatePatientPage: <CreatePatientPage />,
  PatientsPage: <PatientsPage />,
  PatientHistoryPage: <PatientHistoryPage />,
  PharmacyBatchesPage: <PharmacyBatchesPage />,
  PharmacyPOSPage: <PharmacyPOSPage />,
  PharmacySalesHistoryPage: <PharmacySalesHistoryPage />,
  PharmacyBranchesPage: <PharmacyBranchesPage />,
  PharmacyReportsPage: <PharmacyReportsPage />,
  PharmacyDispensingPage: <PharmacyDispensingPage />,
  // Pharmacy Procurement
  PharmacyPurchaseOrdersPage: <PharmacyPurchaseOrdersPage />,
  PharmacyPurchaseOrderDetailPage: <PharmacyPurchaseOrderDetailPage />,
  PharmacyStockTransfersPage: <PharmacyStockTransfersPage />,
  PharmacyCreateStockTransferPage: <PharmacyCreateStockTransferPage />,
  PharmacyStockTransferDetailPage: <PharmacyStockTransferDetailPage />,
  PharmacyGoodsReceivingPage: <PharmacyGoodsReceivingPage />,
  PharmacyGoodsReceivingCreatePage: <PharmacyGoodsReceivingCreatePage />,
  PharmacyGoodsReceivingDetailPage: <PharmacyGoodsReceivingDetailPage />,

  // Retail pages
  RetailInventoryPage: <RetailInventoryPage />,
  RetailSuppliersPage: <RetailSuppliersPage />,
  RetailReportsPage: <RetailReportsPage />,

  // Branch Management pages
  BranchPricingPage: <BranchPricingPage />,
  BranchTransfersPage: <BranchTransfersPage />,

  BarDashboardPage: <BarDashboardPage />,
  BarPOSPage: <BarPOSPage />,
  ComptoirsPage: <ComptoirsPage />,
  BarStockPage: <BarStockPage />,
  BarMovementsPage: <BarMovementsPage />,
  BarRecipesPage: <BarRecipesPage />,
  BarRecipeDetailsPage: <BarRecipeDetailsPage />,
  BarCreateRecipePage: <BarCreateRecipePage />,
  BarTabsPage: <BarTabsPage />,
  BarShiftsPage: <BarShiftsPage />,
  BarAnalyticsPage: <BarAnalyticsPage />,
  BarMenusPage: <BarMenusPage />,
  BarManageMenuPage: <BarManageMenuPage />,
  BarBottlesPage: <BarBottlesPage />,
  
  // Analytics Pages
  SalesAnalyticsPage: <SalesAnalyticsPage />,
  InventoryAnalyticsPage: <InventoryAnalyticsPage />,
  CustomerAnalyticsPage: <CustomerAnalyticsPage />,
  FinancialAnalyticsPage: <FinancialAnalyticsPage />,
  StockMovementsPage: <StockMovementsPage />,
  ExpensesPage: <ExpensesPage />,
  AuditLogsPage: <AuditLogsPage />,
  ReportsPage: <ReportsPage />,
  // Owner Dashboard
  OwnerDashboardPage: <OwnerDashboardPage />,
  // Website Management
  WebsitePricingPlansPage: <WebsitePricingPlansPage />,
  WebsiteVisitorsPage: <WebsiteVisitorsPage />,
  SubscriptionRenewPage: <SubscriptionRenewPage />,
  // Super Admin Portal
  SuperAdminLoginPage: <SuperAdminLoginPage />,
  SuperAdminDashboardPage: <SuperAdminDashboardPage />,
  SuperAdminShopsPage: <SuperAdminShopsPage />,
  SuperAdminUsersPage: <SuperAdminUsersPage />,
  SuperAdminActivityPage: <SuperAdminActivityPage />,
  SuperAdminPaymentsPage: <SuperAdminPaymentsPage />,
  SuperAdminSettingsPage: <SuperAdminSettingsPage />,
  SuperAdminStorefrontsPage: <SuperAdminStorefrontsPage />,
  MerchantStorefrontPage: <MerchantStorefrontPage />,

  // Restaurant Pages
  RestaurantDashboardPage: <RestaurantDashboardPage />,
  RestaurantTablesPage: <RestaurantTablesPage />,
  RestaurantReservationsPage: <RestaurantReservationsPage />,
  RestaurantKitchenPage: <RestaurantKitchenPage />,
  RestaurantTableReportsPage: <RestaurantTableReportsPage />,
  GuestMenuPage: <GuestMenuPage />,
  RestaurantTableTermsPage: <RestaurantTableTermsPage />,

  // Hospital Pages
  HospitalQueuePage: <HospitalQueuePage />,
  HospitalTriagePage: <HospitalTriagePage />,
  HospitalConsultationPage: <HospitalConsultationPage />,
  HospitalClinicalDashboardPage: <HospitalDashboardPage />,
  HospitalReportsPage: <HospitalReportsPage />,
  HospitalWardsPage: <HospitalWardsPage />,
  HospitalLabsPage: <HospitalLabsPage />,
  HospitalAppointmentsPage: <HospitalAppointmentsPage />,
  HospitalFinancePage: <HospitalFinancePage />,
  HospitalPACSPage: <HospitalPACSPage />,
  HospitalSettingsPage: <HospitalSettingsPage />,
  HospitalAmbulancePage: <HospitalAmbulancePage />,
  HRMEmployeesPage: <HRMEmployeesPage />,
  HRMPayrollPage: <HRMPayrollPage />,

  // Domain Microservices Pages
  // Coffee Shop Pages
  CoffeeShopDashboardPage: <CoffeeShopDashboardPage />,
  CoffeeShopPOSPage: <CoffeeShopPOSPage />,
  CoffeeShopOrdersPage: <CoffeeShopOrdersPage />,
  CoffeeShopRecipesPage: <CoffeeShopRecipesPage />,
  CoffeeShopModifiersPage: <CoffeeShopModifiersPage />,
  CoffeeShopShiftsPage: <CoffeeShopShiftsPage />,
  CoffeeShopSpillagePage: <CoffeeShopSpillagePage />,
  CoffeeShopLoyaltyPage: <CoffeeShopLoyaltyPage />,

  GroceryPage: <GroceryPage />,
  GroceryDashboardPage: <GroceryDashboardPage />,
  GroceryPOSPage: <GroceryPOSPage />,
  GroceryInventoryPage: <GroceryInventoryPage />,
  GroceryExpiriesPage: <GroceryExpiriesPage />,
  GrocerySpoilagePage: <GrocerySpoilagePage />,
  GrocerySalesHistoryPage: <GrocerySalesHistoryPage />,
  GroceryReportsPage: <GroceryReportsPage />,
  ClothingPage: <ClothingPage />,
  ShoePage: <ShoeDashboardPage />,
  ShoeCatalogPage: <ShoeCatalogPage />,
  ShoePrepacksPage: <ShoePrepacksPage />,
  ShoeAuditsPage: <ShoeAuditsPage />,
  ShoeMismatchedPage: <ShoeMismatchedPage />,
  ShoeCarePage: <ShoeCarePage />,
  ShoeLabelsPage: <ShoeLabelsPage />,
  ShoeSizeConverterPage: <ShoeSizeConverterPage />,
  ShoePOSPage: <ShoePOSPage />,
  ShoeSalesHistoryPage: <ShoeSalesHistoryPage />,
  ShoeDebtsPage: <ShoeDebtsPage />,
  HardwarePage: <HardwarePage />,
  HardwareDashboardPage: <HardwareDashboardPage />,
  HardwareRentalsPage: <HardwareRentalsPage />,
  HardwareDebtsPage: <HardwareDebtsPage />,
  HardwareAnalyticsPage: <HardwareAnalyticsPage />,
  HardwareCustomersPage: <HardwareCustomersPage />,
  HardwareReportsPage: <HardwareReportsPage />,
  JewelryPage: <JewelryPage />,
  JewelryVaultPage: <JewelryVaultPage />,
  JewelryPOSPage: <JewelryPOSPage />,
  JewelrySalesHistoryPage: <JewelrySalesHistoryPage />,
  JewelryCertificatesPage: <JewelryCertificatesPage />,
  JewelryTradeInsPage: <JewelryTradeInsPage />,
  JewelrySpotRatesPage: <JewelrySpotRatesPage />,
  JewelryReportsPage: <JewelryReportsPage />,
  HotelPage: <HotelPage />,
  HotelDashboardPage: <HotelDashboardPage />,
  HotelRoomsPage: <HotelRoomsPage />,
  HotelReservationsPage: <HotelReservationsPage />,
  HotelHousekeepingPage: <HotelHousekeepingPage />,
  HotelFoliosPage: <HotelFoliosPage />,
  HotelReportsPage: <HotelReportsPage />,
  HotelGuestsPage: <HotelGuestsPage />,

  // 7 New Business Vertical Microservice Pages
  RealEstateWorkspacePage: <RealEstateWorkspacePage />,
  BarbershopWorkspacePage: <BarbershopWorkspacePage />,
  SpaWorkspacePage: <SpaWorkspacePage />,
  TailoringWorkspacePage: <TailoringWorkspacePage />,
  ProfessionalServicesWorkspacePage: <ProfessionalServicesWorkspacePage />,
  SalonWorkspacePage: <SalonWorkspacePage />,
  CarRentalWorkspacePage: <CarRentalWorkspacePage />,

  // Mobile Money & Agency Banking Pages
  MobileMoneyDashboardPage: (
    <GlobalLayout isCustomLayout>
      <MobileMoneyDashboardPage />
    </GlobalLayout>
  ),
  MobileMoneyPOSPage: (
    <GlobalLayout isCustomLayout>
      <MobileMoneyPOSPage />
    </GlobalLayout>
  ),
  MobileMoneyFloatsPage: (
    <GlobalLayout isCustomLayout>
      <MobileMoneyFloatsPage />
    </GlobalLayout>
  ),
  MobileMoneyLedgerPage: (
    <GlobalLayout isCustomLayout>
      <MobileMoneyLedgerPage />
    </GlobalLayout>
  ),
  MobileMoneyReconciliationPage: (
    <GlobalLayout isCustomLayout>
      <MobileMoneyReconciliationPage />
    </GlobalLayout>
  ),
  MobileMoneyDebtsPage: (
    <GlobalLayout isCustomLayout>
      <MobileMoneyDebtsPage />
    </GlobalLayout>
  ),
  MobileMoneyCustomerHistoryPage: (
    <GlobalLayout isCustomLayout>
      <MobileMoneyCustomerHistoryPage />
    </GlobalLayout>
  ),
  MobileMoneyReportsPage: (
    <GlobalLayout isCustomLayout>
      <MobileMoneyReportsPage />
    </GlobalLayout>
  ),
};

export default FukoWebAppPages;
